import React, { useEffect, useState } from 'react';
import { RadioGroup } from '@headlessui/react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';

import {
    setConnectedMediaDevices,
    setPreferredRingtoneAudioOutputId,
} from 'flow/actions/headset-actions';
import useAppContext from 'flow/hooks/useAppContext';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { ConnectedMediaDevice } from 'flow/types/Headset';
import { routes } from 'flow/types/routes';

import {
    askAndGetConnectedMediaDevices,
    filterMediaDevicesBySpecificKind,
} from 'flow/utils/media-utils';
import { generatePath } from 'flow/utils/router-utils';
import DevicePickerRow from '../DevicePickerRow';
import Page from '../Page';

const AdditionalOutputDevice = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();

    const [error, setError] = useState<any>('');
    const preferredRingtoneMicrophoneId = useAppSelector((state) =>
        state.headsets.get('preferredRingtoneAudioOutputId'),
    );
    const audioOutputs = useAppSelector((state) =>
        filterMediaDevicesBySpecificKind(
            state.headsets.get('connectedMediaDevices'),
            'audiooutput',
        ),
    );

    useEffect(() => {
        askAndGetConnectedMediaDevices(true, false)
            .then((devices) => dispatch(setConnectedMediaDevices(devices)))
            .catch((error) => setError(error));
    }, []);

    const audioOutputOptions = [
        {
            deviceId: 'null',
            kind: 'audiooutput',
            label: 'media.settings.ringtone.additionaloutput.none',
        },
        ...audioOutputs,
    ];

    const getErrorMessage = () => {
        const errorName = error.name || error;

        if (errorName === 'NotAllowedError') {
            return i18next.t('Access to microphone devices denied');
        }

        if (errorName === 'PermissionDismissedError') {
            return i18next.t('Access to microphone devices must be given');
        }

        return `An error occurred: ${errorName}`;
    };

    return (
        <Page
            header="media.settings.ringtone.additionaloutput"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.softphone.ringtone'], {
                        extensionKey,
                    }),
                )
            }
        >
            {error ? (
                <div className="text-red">{getErrorMessage()}</div>
            ) : (
                <RadioGroup
                    value={preferredRingtoneMicrophoneId ?? null}
                    aria-label={i18next.t(
                        'media.settings.ringtone.additionaloutput',
                    )}
                    onChange={(deviceId: string) =>
                        dispatch(setPreferredRingtoneAudioOutputId(deviceId))
                    }
                >
                    {audioOutputOptions?.map(
                        (speaker: ConnectedMediaDevice) => (
                            <DevicePickerRow
                                key={speaker.deviceId}
                                deviceId={speaker.deviceId}
                                label={
                                    i18next.t(speaker.label) ||
                                    i18next.t('status.unknown')
                                }
                                selected={
                                    speaker.deviceId ===
                                    preferredRingtoneMicrophoneId
                                }
                            />
                        ),
                    )}
                </RadioGroup>
            )}
        </Page>
    );
};

export default AdditionalOutputDevice;
